/**
 * Hooks React compartilhados entre a web (v1) e o app nativo (fase 4).
 * Só React; nada de DOM nem de React Native.
 */
import { createContext, createElement, type ReactNode, useContext, useEffect, useRef, useState } from 'react';
import { useStore } from 'zustand';
import type { LindezaRuntime, SessionState } from '../app/runtime';
import { conflictIds, findOverlaps, isActive } from '../domain/appointments';
import { type DaySlots, computeSlots, openRangesForDay } from '../domain/availability';
import { cashSummary, receivables, totalReceivable } from '../domain/cash';
import { lastVisits, missingClients } from '../domain/missing';
import { packageBalance } from '../domain/packages';
import { type SaleReceivable, saleItemsLabel, saleReceivables } from '../domain/stock';
import { accessState } from '../domain/subscription';
import {
  type LocalDate,
  addDays,
  dayBoundsUtc,
  endOfMonth,
  startOfMonth,
  todayIn,
  toIso,
  toMs,
  utcToZoned,
} from '../domain/time';
import type {
  Appointment,
  AvailabilityRule,
  AvailabilityWindow,
  Block,
  Client,
  EntityMap,
  EntityType,
  MessageTemplate,
  Package,
  Payment,
  Professional,
  Service,
} from '../schemas/entities';
import type { SyncStatus } from '../sync/engine';

const RuntimeContext = createContext<LindezaRuntime | null>(null);

export function LindezaProvider({ runtime, children }: { runtime: LindezaRuntime; children: ReactNode }) {
  return createElement(RuntimeContext.Provider, { value: runtime }, children);
}

export function useRuntime(): LindezaRuntime {
  const rt = useContext(RuntimeContext);
  if (!rt) throw new Error('LindezaProvider ausente.');
  return rt;
}

export function useSession<T = SessionState>(selector?: (s: SessionState) => T): T {
  const rt = useRuntime();
  return useStore(rt.session, selector ?? ((s) => s as unknown as T));
}

export function useSyncStatus(): SyncStatus {
  const rt = useRuntime();
  const [status, setStatus] = useState(rt.sync.getStatus());
  useEffect(() => rt.sync.subscribe(setStatus), [rt]);
  return status;
}

/**
 * Consulta reativa ao banco local: roda de novo sempre que uma das entidades muda
 * (no aparelho, em outra aba ou pela sincronização).
 */
export function useLiveQuery<T>(
  query: (rt: LindezaRuntime) => Promise<T>,
  deps: unknown[],
  entities?: EntityType[],
): T | undefined {
  const rt = useRuntime();
  const [value, setValue] = useState<T | undefined>(undefined);
  const queryRef = useRef(query);
  queryRef.current = query;
  const entitiesKey = entities?.join(',') ?? '*';

  useEffect(() => {
    let cancelled = false;
    let version = 0;
    const run = () => {
      const v = ++version;
      queryRef.current(rt).then(
        (result) => {
          if (!cancelled && v === version) setValue(result);
        },
        () => undefined,
      );
    };
    run();
    const watched = entities ? new Set(entities) : null;
    const unsubscribe = rt.db.subscribe((changed) => {
      if (!watched || [...changed].some((e) => watched.has(e))) run();
    });
    return () => {
      cancelled = true;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rt, entitiesKey, ...deps]);

  return value;
}

export function useEntities<E extends EntityType>(entity: E): EntityMap[E][] | undefined {
  return useLiveQuery((rt) => rt.db.all(entity), [entity], [entity]);
}

export function useProfessional(): Professional | undefined {
  const id = useSession((s) => s.professionalId);
  return useLiveQuery(async (rt) => (id ? rt.db.get('professional', id) : undefined), [id], ['professional']);
}

export function useAccess() {
  const prof = useProfessional();
  const [now] = useState(() => Date.now());
  if (!prof) return null;
  return accessState(prof, now);
}

export function useServices(options: { includeInactive?: boolean } = {}): Service[] | undefined {
  const include = options.includeInactive ?? false;
  return useLiveQuery(
    async (rt) =>
      (await rt.db.all('service'))
        .filter((s) => include || s.active)
        .sort((a, b) => a.sort_order - b.sort_order || a.name.localeCompare(b.name)),
    [include],
    ['service'],
  );
}

export function useClients(): Client[] | undefined {
  return useLiveQuery(
    async (rt) => (await rt.db.all('client')).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
    [],
    ['client'],
  );
}

export function useTemplates(): MessageTemplate[] | undefined {
  return useEntities('message_template');
}

export function useRules(): AvailabilityRule[] | undefined {
  return useLiveQuery(
    async (rt) => (await rt.db.all('availability_rule')).sort((a, b) => a.weekday - b.weekday || a.start.localeCompare(b.start)),
    [],
    ['availability_rule'],
  );
}

export function useWindows(from: LocalDate, to: LocalDate): AvailabilityWindow[] | undefined {
  return useLiveQuery(
    async (rt) =>
      (await rt.db.range('availability_window', 'date', from, to)).sort(
        (a, b) => a.date.localeCompare(b.date) || a.start.localeCompare(b.start),
      ),
    [from, to],
    ['availability_window'],
  );
}

export interface AgendaItem {
  appointment: Appointment;
  client: Client | undefined;
  service: Service | undefined;
  conflict: boolean;
  packageBalance: { remaining: number; total: number } | null;
}

export interface DayAgenda {
  date: LocalDate;
  timezone: string;
  items: AgendaItem[];
  /** cancelados e faltas do dia (mostrados à parte) */
  inactive: AgendaItem[];
  blocks: Block[];
  openRanges: Array<{ start: string; end: string; kind: 'range' | 'home_window' }>;
  overlaps: Array<[AgendaItem, AgendaItem]>;
}

/** Tudo da tela Agenda para um dia. */
export function useDayAgenda(date: LocalDate): DayAgenda | undefined {
  return useLiveQuery(
    async (rt) => {
      const prof = await rt.db.get('professional', rt.db.professionalId());
      if (!prof) return undefined;
      const tz = prof.timezone;
      const { start, end } = dayBoundsUtc(date, tz);
      const lower = toIso(start - 24 * 3_600_000);
      const upper = toIso(end);
      const [appts, blocks, rules, windows, clients, services, packages, allAppts] = await Promise.all([
        rt.db.range('appointment', 'start_utc', lower, upper),
        rt.db.all('block'),
        rt.db.all('availability_rule'),
        rt.db.range('availability_window', 'date', date, date),
        rt.db.all('client'),
        rt.db.all('service'),
        rt.db.all('package'),
        rt.db.all('appointment'),
      ]);
      const ofDay = appts.filter((a) => toMs(a.start_utc) < end && toMs(a.end_utc) > start);
      const clientById = new Map(clients.map((c) => [c.id, c]));
      const serviceById = new Map(services.map((s) => [s.id, s]));
      const today = todayIn(tz);
      const conflicted = conflictIds(ofDay);
      const toItem = (a: Appointment): AgendaItem => {
        const pkgs = packages.filter((p) => p.client_id === a.client_id && p.service_id === a.service_id);
        const pkg = a.package_id ? packages.find((p) => p.id === a.package_id) : pkgs[0];
        const balance = pkg ? packageBalance(pkg, allAppts, today) : null;
        return {
          appointment: a,
          client: clientById.get(a.client_id),
          service: serviceById.get(a.service_id),
          conflict: conflicted.has(a.id),
          packageBalance: balance ? { remaining: balance.remaining, total: balance.total } : null,
        };
      };
      const sorted = ofDay.sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc));
      const items = sorted.filter((a) => a.status !== 'canceled' && a.status !== 'no_show').map(toItem);
      const inactive = sorted.filter((a) => a.status === 'canceled' || a.status === 'no_show').map(toItem);
      const itemById = new Map(items.map((i) => [i.appointment.id, i]));
      const overlaps = findOverlaps(ofDay)
        .map(([a, b]) => [itemById.get(a.id), itemById.get(b.id)] as const)
        .filter((p): p is readonly [AgendaItem, AgendaItem] => Boolean(p[0] && p[1]))
        .map((p) => [p[0], p[1]] as [AgendaItem, AgendaItem]);
      return {
        date,
        timezone: tz,
        items,
        inactive,
        blocks: blocks
          .filter((b) => toMs(b.start_utc) < end && toMs(b.end_utc) > start)
          .sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc)),
        openRanges: openRangesForDay(date, prof, rules, windows),
        overlaps,
      };
    },
    [date],
    ['appointment', 'block', 'availability_rule', 'availability_window', 'client', 'service', 'package', 'professional'],
  );
}

/** Quantos atendimentos ativos há em cada dia de um intervalo (para a faixa de dias). */
export function useDayCounts(from: LocalDate, to: LocalDate): Map<LocalDate, number> | undefined {
  return useLiveQuery(
    async (rt) => {
      const prof = await rt.db.get('professional', rt.db.professionalId());
      if (!prof) return new Map();
      const lower = toIso(dayBoundsUtc(from, prof.timezone).start);
      const upper = toIso(dayBoundsUtc(to, prof.timezone).end);
      const appts = await rt.db.range('appointment', 'start_utc', lower, upper);
      const out = new Map<LocalDate, number>();
      for (const a of appts) {
        if (!isActive(a) && a.status !== 'completed') continue;
        const d = utcToZoned(a.start_utc, prof.timezone).date;
        out.set(d, (out.get(d) ?? 0) + 1);
      }
      return out;
    },
    [from, to],
    ['appointment', 'professional'],
  );
}

export interface ClientDetail {
  client: Client;
  history: Array<{ appointment: Appointment; service: Service | undefined; paid: number }>;
  upcoming: Array<{ appointment: Appointment; service: Service | undefined }>;
  lastVisitAt: string | null;
  noShows: number;
  totalSpent: number;
  packages: Array<{ pkg: Package; service: Service | undefined; remaining: number; used: number; expired: boolean }>;
  receivable: number;
}

export function useClientDetail(id: string): ClientDetail | null | undefined {
  return useLiveQuery(
    async (rt) => {
      const client = await rt.db.get('client', id);
      if (!client) return null;
      const prof = await rt.db.get('professional', rt.db.professionalId());
      const tz = prof?.timezone ?? 'America/Sao_Paulo';
      const [appointments, services, payments, packages] = await Promise.all([
        rt.db.all('appointment'),
        rt.db.all('service'),
        rt.db.all('payment'),
        rt.db.all('package'),
      ]);
      const serviceById = new Map(services.map((s) => [s.id, s]));
      const mine = appointments.filter((a) => a.client_id === id);
      const now = Date.now();
      const paidBy = new Map<string, number>();
      for (const p of payments) if (p.appointment_id) paidBy.set(p.appointment_id, (paidBy.get(p.appointment_id) ?? 0) + p.amount_cents);
      const history = mine
        .filter((a) => toMs(a.start_utc) < now || a.status === 'completed')
        .sort((a, b) => toMs(b.start_utc) - toMs(a.start_utc))
        .map((a) => ({ appointment: a, service: serviceById.get(a.service_id), paid: paidBy.get(a.id) ?? 0 }));
      const upcoming = mine
        .filter((a) => isActive(a) && toMs(a.end_utc) >= now)
        .sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc))
        .map((a) => ({ appointment: a, service: serviceById.get(a.service_id) }));
      const today = todayIn(tz);
      const rec = receivables(mine, payments);
      return {
        client,
        history,
        upcoming,
        lastVisitAt: lastVisits(mine).get(id)?.at ?? client.last_visit_at,
        noShows: mine.filter((a) => a.status === 'no_show').length,
        totalSpent: payments.filter((p) => p.client_id === id).reduce((s, p) => s + p.amount_cents, 0),
        packages: packages
          .filter((p) => p.client_id === id)
          .map((pkg) => {
            const b = packageBalance(pkg, appointments, today);
            return { pkg, service: serviceById.get(pkg.service_id), remaining: b.remaining, used: b.used, expired: b.expired };
          }),
        receivable: totalReceivable(rec),
      };
    },
    [id],
    ['client', 'appointment', 'service', 'payment', 'package'],
  );
}

export function useMissingClients() {
  return useLiveQuery(
    async (rt) => {
      const prof = await rt.db.get('professional', rt.db.professionalId());
      const [clients, appointments, services] = await Promise.all([
        rt.db.all('client'),
        rt.db.all('appointment'),
        rt.db.all('service'),
      ]);
      return missingClients({
        clients,
        appointments,
        services,
        defaultDays: prof?.missing_after_days ?? 21,
        now: Date.now(),
      });
    },
    [],
    ['client', 'appointment', 'service', 'professional'],
  );
}

export interface CashView {
  today: LocalDate;
  day: ReturnType<typeof cashSummary>;
  month: ReturnType<typeof cashSummary>;
  receivables: Array<ReturnType<typeof receivables>[number] & { client: Client | undefined; service: Service | undefined }>;
  /** vendas de produto com "Receber depois" */
  saleReceivables: Array<SaleReceivable & { client: Client | undefined; label: string }>;
  /** atendimentos e vendas de produto */
  receivableTotal: number;
  recentPayments: Array<{ payment: Payment; client: Client | undefined }>;
}

export function useCash(date?: LocalDate): CashView | undefined {
  return useLiveQuery(
    async (rt) => {
      const prof = await rt.db.get('professional', rt.db.professionalId());
      const tz = prof?.timezone ?? 'America/Sao_Paulo';
      const today = date ?? todayIn(tz);
      const [payments, appointments, clients, services, sales, movements, products] = await Promise.all([
        rt.db.all('payment'),
        rt.db.all('appointment'),
        rt.db.all('client'),
        rt.db.all('service'),
        rt.db.all('sale'),
        rt.db.all('stock_movement'),
        rt.db.all('product'),
      ]);
      const clientById = new Map(clients.map((c) => [c.id, c]));
      const serviceById = new Map(services.map((s) => [s.id, s]));
      const rec = receivables(appointments, payments).map((r) => ({
        ...r,
        client: clientById.get(r.appointment.client_id),
        service: serviceById.get(r.appointment.service_id),
      }));
      const saleRec = saleReceivables(sales, payments).map((r) => ({
        ...r,
        client: r.sale.client_id ? clientById.get(r.sale.client_id) : undefined,
        label: saleItemsLabel(r.sale.id, movements, products),
      }));
      const { start, end } = dayBoundsUtc(today, tz);
      return {
        today,
        day: cashSummary(payments, today, today, tz),
        month: cashSummary(payments, startOfMonth(today), endOfMonth(today), tz),
        receivables: rec,
        saleReceivables: saleRec,
        receivableTotal: totalReceivable(rec) + saleRec.reduce((sum, r) => sum + r.missing, 0),
        recentPayments: payments
          .filter((p) => toMs(p.paid_at) >= start && toMs(p.paid_at) < end)
          .sort((a, b) => toMs(b.paid_at) - toMs(a.paid_at))
          .map((payment) => ({ payment, client: payment.client_id ? clientById.get(payment.client_id) : undefined })),
      };
    },
    [date],
    ['payment', 'appointment', 'client', 'service', 'professional', 'sale', 'stock_movement', 'product'],
  );
}

/** Atendimentos de amanhã para o "Enviar lembretes" (seção 11.2). */
export function useTomorrow() {
  return useLiveQuery(
    async (rt) => {
      const prof = await rt.db.get('professional', rt.db.professionalId());
      if (!prof) return [];
      const tomorrow = addDays(todayIn(prof.timezone), 1);
      const { start, end } = dayBoundsUtc(tomorrow, prof.timezone);
      const [appts, clients, services] = await Promise.all([
        rt.db.range('appointment', 'start_utc', toIso(start), toIso(end - 1)),
        rt.db.all('client'),
        rt.db.all('service'),
      ]);
      const clientById = new Map(clients.map((c) => [c.id, c]));
      const serviceById = new Map(services.map((s) => [s.id, s]));
      return appts
        .filter((a) => isActive(a) && a.status !== 'pending')
        .sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc))
        .map((a) => ({ appointment: a, client: clientById.get(a.client_id), service: serviceById.get(a.service_id) }));
    },
    [],
    ['appointment', 'client', 'service', 'professional'],
  );
}

/** Pedidos do link aguardando aprovação. */
export function usePendingRequests() {
  return useLiveQuery(
    async (rt) => {
      const [appts, clients, services] = await Promise.all([
        rt.db.all('appointment'),
        rt.db.all('client'),
        rt.db.all('service'),
      ]);
      const clientById = new Map(clients.map((c) => [c.id, c]));
      const serviceById = new Map(services.map((s) => [s.id, s]));
      return appts
        .filter((a) => a.status === 'pending' && toMs(a.start_utc) > Date.now() - 3_600_000)
        .sort((a, b) => toMs(a.start_utc) - toMs(b.start_utc))
        .map((a) => ({ appointment: a, client: clientById.get(a.client_id), service: serviceById.get(a.service_id) }));
    },
    [],
    ['appointment', 'client', 'service'],
  );
}

/**
 * Horários livres para a própria profissional (remarcar, marcar em outro dia).
 * Mesma regra do link, mas sem antecedência mínima.
 */
export function useFreeSlots(params: {
  durationMin: number;
  location: 'studio' | 'home';
  from: LocalDate;
  to: LocalDate;
  ignoreAppointmentId?: string;
}): DaySlots[] | undefined {
  const { durationMin, location, from, to, ignoreAppointmentId } = params;
  return useLiveQuery(
    async (rt) => {
      const prof = await rt.db.get('professional', rt.db.professionalId());
      if (!prof) return [];
      const lower = toIso(dayBoundsUtc(addDays(from, -1), prof.timezone).start);
      const upper = toIso(dayBoundsUtc(addDays(to, 1), prof.timezone).end);
      const [rules, windows, appointments, blocks] = await Promise.all([
        rt.db.all('availability_rule'),
        rt.db.range('availability_window', 'date', from, to),
        rt.db.range('appointment', 'start_utc', lower, upper),
        rt.db.all('block'),
      ]);
      return computeSlots({
        settings: { ...prof, min_notice_min: 0, max_days_ahead: Math.max(prof.max_days_ahead, 90) },
        rules,
        windows,
        appointments,
        blocks,
        durationMin,
        location,
        from,
        to,
        now: Date.now(),
        ...(ignoreAppointmentId ? { ignoreAppointmentId } : {}),
      });
    },
    [durationMin, location, from, to, ignoreAppointmentId],
    ['availability_rule', 'availability_window', 'appointment', 'block', 'professional'],
  );
}

export interface ClientPick {
  client: Client;
  lastServiceId: string | null;
  lastAt: string | null;
  visits: number;
}

/** Clientes para escolher ao marcar: quem veio mais recente primeiro, com o último serviço. */
export function useClientPicker(): ClientPick[] | undefined {
  return useLiveQuery(
    async (rt) => {
      const [clients, appointments] = await Promise.all([rt.db.all('client'), rt.db.all('appointment')]);
      const last = new Map<string, { at: string; serviceId: string; visits: number }>();
      for (const a of appointments) {
        if (a.status === 'canceled') continue;
        const prev = last.get(a.client_id);
        const visits = (prev?.visits ?? 0) + 1;
        if (!prev || a.start_utc > prev.at) last.set(a.client_id, { at: a.start_utc, serviceId: a.service_id, visits });
        else last.set(a.client_id, { ...prev, visits });
      }
      return clients
        .map((client) => {
          const l = last.get(client.id);
          return { client, lastServiceId: l?.serviceId ?? null, lastAt: l?.at ?? null, visits: l?.visits ?? 0 };
        })
        .sort((a, b) => (b.lastAt ?? '').localeCompare(a.lastAt ?? '') || a.client.name.localeCompare(b.client.name, 'pt-BR'));
    },
    [],
    ['client', 'appointment'],
  );
}

export function useOutboxCount(): number {
  return useSyncStatus().pending;
}

export { type WeekDay, useWeekAgenda } from './week';
export * from './stock';
